import { useEffect, useState, useCallback, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { getToken, onMessage, MessagePayload } from 'firebase/messaging';
import { toast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { getFirebaseMessaging, VAPID_KEY } from '@/lib/firebase/config';
import { pushNotificationsApi } from '@/lib/api/services/pushNotifications';
import { useAuth } from '@/components/AuthProvider';

const STORAGE_KEY_TOKEN = 'fcm_web_token';
const STORAGE_KEY_DENIED = 'fcm_permission_denied';
// Per-user explicit-disable marker. Set when the user clicks "Disable Notifications";
// cleared when they click "Enable Notifications". Survives logout/login on the same
// browser+account so the user's choice isn't silently undone by auto-register.
// Keyed by userId so shared computers don't leak one user's preference to others.
const STORAGE_KEY_DISABLED_PREFIX = 'fcm_disabled_by_user_';

/**
 * Returns true if the given user has explicitly disabled push notifications on this
 * browser (via the "Disable Notifications" button). PushNotificationManager checks
 * this before silently auto-registering a token.
 */
export function isPushDisabledByUser(userId: string | undefined | null): boolean {
  if (!userId) return false;
  try {
    return localStorage.getItem(STORAGE_KEY_DISABLED_PREFIX + userId) === 'true';
  } catch {
    return false;
  }
}

// Custom event fired whenever the master push state flips (token registered or
// unregistered). Lets read-only consumers (e.g. NotificationPreferencesCard) react
// without instantiating the full usePushNotifications hook — instantiating it twice
// would register duplicate Firebase onMessage listeners and produce duplicate toasts.
const PUSH_STATE_CHANGE_EVENT = 'fcm:state-changed';

function readMasterEnabled(): boolean {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') return false;
  try {
    return (
      Notification.permission === 'granted' &&
      Boolean(localStorage.getItem(STORAGE_KEY_TOKEN))
    );
  } catch {
    return false;
  }
}

/**
 * Read-only hook returning whether push notifications are currently enabled at the
 * master level (browser permission granted AND a token is registered). Updates
 * reactively when usePushNotifications.unregister() or .requestPermission() flip
 * the state, including across tabs (via the storage event).
 *
 * Use this in components that need to gate UI on push state without owning the
 * full lifecycle (registration, foreground listener, etc.).
 */
export function useIsPushMasterEnabled(): boolean {
  const [enabled, setEnabled] = useState<boolean>(() => readMasterEnabled());

  useEffect(() => {
    const refresh = () => setEnabled(readMasterEnabled());
    // Same-tab updates: usePushNotifications dispatches this on register/unregister.
    window.addEventListener(PUSH_STATE_CHANGE_EVENT, refresh);
    // Cross-tab updates: storage events fire when *other* tabs touch localStorage.
    window.addEventListener('storage', refresh);
    // Refresh once on mount in case state changed before the listener attached.
    refresh();
    return () => {
      window.removeEventListener(PUSH_STATE_CHANGE_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  return enabled;
}

function dispatchPushStateChange() {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new Event(PUSH_STATE_CHANGE_EVENT));
  } catch {
    /* ignore — older browsers / SSR */
  }
}

interface UsePushNotificationsOptions {
  /** Auto-request permission when user logs in (default: false — ask user explicitly). */
  autoRequest?: boolean;
  /** Called when a foreground notification arrives. */
  onForegroundMessage?: (payload: MessagePayload) => void;
}

interface UsePushNotificationsReturn {
  /** Current browser notification permission. */
  permission: NotificationPermission | 'unsupported';
  /** Registered FCM token (null if not registered). */
  token: string | null;
  /** True while requesting permission or registering token. */
  loading: boolean;
  /**
   * Request permission and register token. Returns true on success.
   * Pass `{ silent: true }` for background auto-registration — failures are
   * logged but show NO toast (the user didn't ask for anything, so don't nag).
   */
  requestPermission: (opts?: { silent?: boolean }) => Promise<boolean>;
  /**
   * Re-read the live browser permission and reconcile UI state. Used after the
   * user changes the permission in their browser settings (which JS cannot
   * detect on its own). If the permission is now 'granted', registers the FCM
   * token; if it dropped back to 'default', the normal "Enable" button returns.
   * Returns the freshly-read permission.
   */
  refreshPermission: () => Promise<NotificationPermission | 'unsupported'>;
  /** Unregister and remove token. */
  unregister: () => Promise<void>;
  /** Whether the browser supports push notifications. */
  isSupported: boolean;
}

export function usePushNotifications(
  options: UsePushNotificationsOptions = {}
): UsePushNotificationsReturn {
  const { autoRequest = false, onForegroundMessage } = options;
  const navigate = useNavigate();
  const { user } = useAuth();

  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
  );
  const [token, setToken] = useState<string | null>(
    () => localStorage.getItem(STORAGE_KEY_TOKEN)
  );
  const [loading, setLoading] = useState(false);

  const isSupported =
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window;

  const requestPermission = useCallback(async (opts?: { silent?: boolean }): Promise<boolean> => {
    const silent = opts?.silent === true;
    if (!isSupported) {
      console.warn('[PushNotifications] Not supported in this browser');
      return false;
    }

    setLoading(true);
    try {
      // 1. Request browser permission
      const perm = await Notification.requestPermission();
      setPermission(perm);

      if (perm !== 'granted') {
        localStorage.setItem(STORAGE_KEY_DENIED, 'true');
        if (!silent) toast({
          title: 'Notifications blocked',
          description: 'You can enable them anytime in your browser settings.',
          variant: 'destructive',
        });
        return false;
      }

      localStorage.removeItem(STORAGE_KEY_DENIED);

      // 2. Get FCM token
      const messaging = await getFirebaseMessaging();
      if (!messaging) {
        console.warn('[PushNotifications] Firebase Messaging not available');
        return false;
      }

      // Explicitly register the Firebase messaging service worker
      // (separate from the Vite PWA service worker)
      const fbSwRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
        scope: '/firebase-cloud-messaging-push-scope',
        updateViaCache: 'none',
      });

      // Wait for it to be active
      if (fbSwRegistration.installing || fbSwRegistration.waiting) {
        await new Promise<void>((resolve) => {
          const worker = fbSwRegistration.installing || fbSwRegistration.waiting;
          if (!worker) return resolve();
          if (worker.state === 'activated') return resolve();
          worker.addEventListener('statechange', () => {
            if (worker.state === 'activated') resolve();
          });
        });
      }

      const fcmToken = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: fbSwRegistration,
      });

      if (!fcmToken) {
        console.warn('[PushNotifications] Failed to get FCM token');
        return false;
      }

      // 3. Register with backend
      await pushNotificationsApi.registerDevice(fcmToken, 'web');
      localStorage.setItem(STORAGE_KEY_TOKEN, fcmToken);
      // Clear the per-user explicit-disable marker — user has just re-enabled
      // notifications, so the auto-register guard should no longer block them.
      if (user?.id) {
        localStorage.removeItem(STORAGE_KEY_DISABLED_PREFIX + user.id);
      }
      setToken(fcmToken);
      // Notify any read-only consumers (e.g. NotificationPreferencesCard) so they
      // can re-render with master = enabled.
      dispatchPushStateChange();

      if (!silent) toast({ variant: "success",
        title: 'Notifications enabled',
        description: 'You will receive push notifications for important events.',
      });

      return true;
    } catch (error: any) {
      console.error('[PushNotifications] Error requesting permission:', error);
      if (!silent) toast({
        title: 'Failed to enable notifications',
        description: error.message || 'Please try again.',
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [isSupported, user?.id]);

  const refreshPermission = useCallback(async (): Promise<NotificationPermission | 'unsupported'> => {
    if (!isSupported || typeof Notification === 'undefined') return 'unsupported';

    const current = Notification.permission;
    setPermission(current);

    if (current === 'granted') {
      // Browser permission was unblocked — clear the denied marker and register
      // the token so notifications actually start flowing.
      localStorage.removeItem(STORAGE_KEY_DENIED);
      if (!localStorage.getItem(STORAGE_KEY_TOKEN)) {
        await requestPermission();
      }
    } else if (current === 'denied') {
      toast({
        title: 'Still blocked',
        description: 'Notifications are still blocked in your browser. Follow the steps above, then re-check.',
        variant: 'destructive',
      });
    } else {
      // 'default' — user reset it to "Ask"; the denied marker no longer applies.
      localStorage.removeItem(STORAGE_KEY_DENIED);
    }

    return current;
  }, [isSupported, requestPermission]);

  const unregister = useCallback(async (): Promise<void> => {
    const storedToken = localStorage.getItem(STORAGE_KEY_TOKEN);
    if (!storedToken) return;

    try {
      await pushNotificationsApi.unregisterDevice(storedToken);
    } catch (error) {
      console.error('[PushNotifications] Failed to unregister:', error);
    } finally {
      localStorage.removeItem(STORAGE_KEY_TOKEN);
      // Set the per-user explicit-disable marker so PushNotificationManager won't
      // silently re-register the token on the next render. Cleared by requestPermission.
      if (user?.id) {
        localStorage.setItem(STORAGE_KEY_DISABLED_PREFIX + user.id, 'true');
      }
      setToken(null);
      // Notify any read-only consumers (e.g. NotificationPreferencesCard) so they
      // can re-render with master = disabled.
      dispatchPushStateChange();
    }
  }, [user?.id]);

  // Listen for foreground messages (when app is open)
  useEffect(() => {
    if (!isSupported || permission !== 'granted') return;

    let unsubscribe: (() => void) | undefined;

    (async () => {
      const messaging = await getFirebaseMessaging();
      if (!messaging) return;

      unsubscribe = onMessage(messaging, (payload) => {
        // Show in-app toast using sonner (no limit, rich UI)
        // Backend uses data-only payloads so title/body live in `data`
        const data = payload.data || {};
        const title = (data.title as string) || payload.notification?.title || 'WeNext';
        const body = (data.body as string) || payload.notification?.body || '';

        // Compute the target URL for click routing (mirrors firebase-messaging-sw.js)
        const type = (data.type || '').toUpperCase();
        let targetUrl: string | null = null;

        if (type === 'MESSAGE_RECEIVED' && data.conversationId) {
          targetUrl = `/home/inbox?conversationId=${data.conversationId}`;
        } else if (['CAMPAIGN_CREATED', 'CAMPAIGN_STARTED', 'CAMPAIGN_COMPLETED', 'CAMPAIGN_COMPLETED_WITH_ERRORS'].includes(type)) {
          targetUrl = '/home/campaigns';
        } else if (['CONTACT_IMPORT_COMPLETED', 'CONTACT_IMPORT_FAILED'].includes(type)) {
          targetUrl = '/home/contacts';
        } else if (['TEMPLATE_APPROVED', 'TEMPLATE_REJECTED'].includes(type)) {
          targetUrl = '/home/templates';
        } else if (type === 'BUSINESS_ACCOUNT_STATUS_CHANGE') {
          targetUrl = '/home/business-accounts';
        } else if (type === 'TEAM_INVITATION') {
          targetUrl = '/home/team-management';
        } else if (type === 'TICKET_ASSIGNED' || type === 'TICKET_COMMENT') {
          targetUrl = '/home/tickets';
        } else if (data.url) {
          targetUrl = data.url;
        }

        toast({
          title,
          description: body,
          duration: 6000,
          variant: 'info',
          action: targetUrl
            ? createElement(
                ToastAction,
                {
                  altText: 'View',
                  onClick: () => {
                    const current = window.location.pathname + window.location.search;
                    if (current !== targetUrl) {
                      navigate(targetUrl!);
                    }
                  },
                },
                'View'
              )
            : undefined,
        });

        if (onForegroundMessage) {
          onForegroundMessage(payload);
        }
      });
    })();

    return () => {
      unsubscribe?.();
    };
  }, [isSupported, permission, onForegroundMessage]);

  // Auto-request if enabled (and permission is 'default', meaning user hasn't decided yet)
  useEffect(() => {
    if (!autoRequest || !isSupported) return;
    if (permission !== 'default') return;
    if (localStorage.getItem(STORAGE_KEY_DENIED) === 'true') return;

    // Delay slightly to avoid prompting during page load
    const timer = setTimeout(() => {
      requestPermission();
    }, 2000);

    return () => clearTimeout(timer);
  }, [autoRequest, isSupported, permission, requestPermission]);

  // Listen for service worker messages (notification clicks)
  useEffect(() => {
    if (!isSupported) return;

    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'NOTIFICATION_CLICK' && event.data?.url) {
        // Use React Router for client-side navigation (no full page reload)
        const url = event.data.url as string;
        // Only navigate if not already on that URL
        const current = window.location.pathname + window.location.search;
        if (current !== url) {
          navigate(url);
        }
      }
    };

    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [isSupported, navigate]);

  return {
    permission,
    token,
    loading,
    requestPermission,
    refreshPermission,
    unregister,
    isSupported,
  };
}
